import { useAuth } from '@/hooks/use-auth';
import { Navigate, Outlet, useLocation } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { SkeletonPagina } from '@/components/ui/skeletons';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCcw } from 'lucide-react';

interface ProtectedRouteProps {
  requireAdmin?: boolean;
}

export function ProtectedRoute({ requireAdmin = false }: ProtectedRouteProps) {
  const { profile, authState } = useAuth();
  const location = useLocation();
  const redirectCountRef = useRef(0);
  const lastRedirectRef = useRef(Date.now());
  const [loopDetected, setLoopDetected] = useState(false);

  useEffect(() => {
    const now = Date.now();
    if (now - lastRedirectRef.current < 2000) {
      redirectCountRef.current += 1;
    } else {
      redirectCountRef.current = 1;
    }
    lastRedirectRef.current = now;

    if (redirectCountRef.current > 5) {
      console.error("Redirection loop detected.");
      setLoopDetected(true);
    }
  }, [location.pathname]);

  if (loopDetected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F9F5] p-4 text-center">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center gap-6">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-[#0C1A12]">Não foi possível carregar sua sessão</h1>
            <p className="text-gray-500 text-sm">Houve um problema ao validar seu acesso. Por favor, tente recarregar a página.</p>
          </div>
          <Button 
            onClick={() => window.location.reload()} 
            className="w-full gap-2"
          >
            <RefreshCcw className="w-4 h-4" />
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  if (authState === 'carregando') {
    return <SkeletonPagina />;
  }

  if (authState === 'anonimo') {
    return <Navigate to="/entrar" replace />;
  }

  if (!profile) {
    return <SkeletonPagina />;
  }

  if (profile.status === 'bloqueado') {
    return <Navigate to="/entrar" replace />;
  }

  if (profile.status === 'pendente' && location.pathname !== '/aguardando') {
    return <Navigate to="/aguardando" replace />;
  }

  if (profile.status === 'nao_cliente' && location.pathname !== '/acesso-negado') {
    return <Navigate to="/acesso-negado" replace />;
  }

  if (requireAdmin && !['admin', 'super_admin'].includes(profile.role)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
